import { useState } from 'react';
import Modal from '@/components/common/Modal';
import Input from '@/components/common/Input';
import TextArea from '@/components/common/TextArea';
import Button from '@/components/common/Button';
import { useCreateSet } from '@/hooks/useSets';

interface CreateSetModalProps {
  isOpen: boolean;
  onClose: () => void;
}

/**
 * Modal for creating a new Set (move category).
 *
 * Contains fields for the Set name and description, with validation.
 */
export default function CreateSetModal({ isOpen, onClose }: CreateSetModalProps) {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [errors, setErrors] = useState<{ name?: string; description?: string }>({});

  const createSet = useCreateSet();

  const validate = (): boolean => {
    const newErrors: { name?: string; description?: string } = {};

    if (!name.trim()) {
      newErrors.name = 'Name is required';
    } else if (name.trim().length > 100) {
      newErrors.name = 'Name cannot exceed 100 characters';
    }

    if (!description.trim()) {
      newErrors.description = 'Description is required';
    } else if (description.trim().length > 500) {
      newErrors.description = 'Description cannot exceed 500 characters';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validate()) return;

    try {
      await createSet.mutateAsync({
        name: name.trim(),
        description: description.trim(),
      });
      handleClose();
    } catch {
      // Error is handled by the mutation's error state
    }
  };

  const handleClose = () => {
    setName('');
    setDescription('');
    setErrors({});
    createSet.reset();
    onClose();
  };

  return (
    <Modal isOpen={isOpen} onClose={handleClose} title="New Set" size="md">
      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label
            htmlFor="set-name"
            className="mb-1 block text-sm font-medium text-neutral-300"
          >
            Name
          </label>
          <Input
            id="set-name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="e.g., Show Booking"
            maxLength={100}
            error={errors.name}
          />
        </div>

        <div>
          <label
            htmlFor="set-description"
            className="mb-1 block text-sm font-medium text-neutral-700"
          >
            Description
          </label>
          <TextArea
            id="set-description"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="Briefly describe what moves in this Set should accomplish..."
            rows={3}
            maxLength={500}
            error={errors.description}
          />
        </div>

        {createSet.isError && (
          <p className="text-sm text-error-600">
            {createSet.error?.message || 'Failed to create Set. Please try again.'}
          </p>
        )}

        <div className="flex justify-end gap-3 pt-2">
          <Button variant="ghost" type="button" onClick={handleClose}>
            Cancel
          </Button>
          <Button
            variant="primary"
            type="submit"
            loading={createSet.isPending}
            disabled={createSet.isPending}
          >
            Create Set
          </Button>
        </div>
      </form>
    </Modal>
  );
}
